/* eslint-disable react/no-unsafe */
// Initializes a video model and renders a KAVideoWrapper with it.
//
// TODO(riley): As the name implies, we want this to use Redux eventually
//              instead of Backbone.
// TODO(riley): What happens when we have several of these on the same page?
//              Probably some spooky side-effects.
import {compose} from "mu-lambda";
import * as React from "react";

import {
    enums,
    withConversionContext,
    withMarkConversion,
    type PartialLearningTimeInfo,
    type WithMarkConversionProps,
    type WithoutMarkConversion,
} from "@khan/analytics-learning-time";
import type {EndOfVideoAttributionType} from "@khan/content-types";

import type {
    TaskType,
    TutorialNavContent,
    TutorialPageTopic,
    VideoContentModel,
} from "#tutorial-types-package/types.ts";

import {VideoPlayerState} from "./constants.ts";
import initializePlayerModel from "./initialize-player-model.ts";
import KAVideoWrapper from "./ka-video-wrapper.tsx";
import type {VideoPlayerModelType, VideoPlayerStateType} from "./types.ts";

// TODO(riley): This is a lot of props. A few of them (`topic`, `userVideo`…)
//              could be replaced with their relevant keys or a single
//              `options` object.
type StatefulVideoWrapperProps = {
    // Whether the player is in an /embed_video frame.
    embedded?: boolean;
    // Used for autoplay.
    navigateToNextItem?: () => unknown;
    // The next item in a tutorial, if it exists.
    // TODO(riley): Share this definition with EndOfVideoCard.
    nextContentItem?: TutorialNavContent | null | undefined;
    // Functions to track video usage and statistics.
    onComponentInteractive?: () => void;
    // If a parent needs to track the model, in can subscribe with this.
    onModelUpdate?: (playerModel: VideoPlayerModelType) => void;
    onStateChanged?: (playerState: VideoPlayerStateType) => void;
    // Override the default behavior to force autoplay on or off.
    shouldAutoplay?: boolean;
    // The current video task.
    task: TaskType | null | undefined;
    // The video's enclosing topic.
    topic?: TutorialPageTopic;
    video: VideoContentModel;
    attribution?: EndOfVideoAttributionType | null | undefined;
};

type Props = StatefulVideoWrapperProps & WithMarkConversionProps;

type State = {
    extraEmbedTag: HTMLElement | null | undefined;
    // WARNING(kevinb): `playerModel` is typed as `any`
    playerModel: VideoPlayerModelType;
};

class StatefulVideoWrapper extends React.Component<Props, State> {
    // TODO(riley): Reduce the amount of duplication from fall through props
    //              for `KAVideoWrapper`.
    constructor(props: Props) {
        super(props);

        const {video, topic, markConversion} = props;
        const playerModel = initializePlayerModel(video, topic, markConversion);

        this.state = {
            extraEmbedTag: null,
            playerModel,
        };
    }

    // Causing side-effects here for parent components is strange, but it lets
    // us encapsulate the reusable model at this level of the component tree.
    // This is convenient; parent components rarely need to see the model.
    // TODO(jangmi, LP-7300): Remove usage of `UNSAFE_componentWillMount`
    UNSAFE_componentWillMount() {
        const {embedded, onModelUpdate} = this.props;
        const {playerModel} = this.state;

        if (onModelUpdate) {
            onModelUpdate(playerModel);
        }

        if (embedded) {
            this.addExtraEmbedTag();
        }
    }

    componentDidMount() {
        window.addEventListener("beforeunload", this.handleWindowBeforeUnload);
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        const {embedded, video, topic, onModelUpdate, markConversion} =
            nextProps;
        const {extraEmbedTag} = this.state;

        // In order to ensure that the video player model can fire events with
        // the most up-to-date learning time context, we need to update the
        // update the model if the markConversion function has changed.
        if (markConversion !== this.props.markConversion) {
            this.setState(({playerModel}) => {
                playerModel.markConversion = markConversion;
                return {playerModel};
            });
        }

        if (video.youtubeId !== this.props.video.youtubeId) {
            const playerModel = initializePlayerModel(
                video,
                topic,
                markConversion,
            );

            this.setState({playerModel}, () => {
                if (onModelUpdate) {
                    onModelUpdate(playerModel);
                }
            });
        }

        if (embedded && !extraEmbedTag) {
            this.addExtraEmbedTag();
        } else if (!embedded && extraEmbedTag) {
            this.removeExtraEmbedTag();
        }
    }

    componentWillUnmount() {
        window.removeEventListener(
            "beforeunload",
            this.handleWindowBeforeUnload,
        );
        this.removeExtraEmbedTag();
    }

    addExtraEmbedTag() {
        const extraEmbedTag = document.createElement("base");
        extraEmbedTag.setAttribute("target", "_blank");
        this.setState({extraEmbedTag}, () => {
            document.head && document.head.appendChild(extraEmbedTag);
        });
    }

    removeExtraEmbedTag() {
        const {extraEmbedTag} = this.state;

        if (extraEmbedTag) {
            document.head && document.head.removeChild(extraEmbedTag);
        }
    }

    handleWindowBeforeUnload: () => void = () => {
        const {playerModel} = this.state;

        // If the video is playing when the window closes, we want to save the
        // video progress. Notify the player model that the video is about to
        // pause, which will trigger a save if the video is not already paused.
        if (playerModel) {
            playerModel.setPlayerState(VideoPlayerState.PAUSED);
        }
    };

    render(): React.ReactNode {
        const {
            embedded,
            navigateToNextItem,
            nextContentItem,
            onComponentInteractive,
            onStateChanged,
            shouldAutoplay,
            task,
            video,
            attribution,
        } = this.props;
        const {playerModel} = this.state;

        return (
            <KAVideoWrapper
                embedded={embedded}
                navigateToNextItem={navigateToNextItem}
                nextContentItem={nextContentItem}
                playerModel={playerModel}
                onComponentInteractive={onComponentInteractive}
                onStateChanged={onStateChanged}
                shouldAutoplay={shouldAutoplay}
                task={task}
                video={{...video}}
                attribution={attribution}
            />
        );
    }
}

const mapPropsToLearningTime = (
    ownProps: StatefulVideoWrapperProps,
): PartialLearningTimeInfo => {
    const {video} = ownProps;
    const contentId = video.id ? video.id : null;
    return {
        activity: enums.Activity.WATCHING,
        content: {
            kind: enums.ContentKind.Video,
            ...(contentId && {contentId}),
            contentTitle: video.translatedTitle,
        },
    };
};

type ExportProps = WithoutMarkConversion<
    JSX.LibraryManagedAttributes<
        typeof StatefulVideoWrapper,
        React.ComponentProps<typeof StatefulVideoWrapper>
    >
>;

export default compose(
    withConversionContext(mapPropsToLearningTime),
    withMarkConversion,
)(StatefulVideoWrapper) as React.ComponentType<ExportProps>;
